import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SkillFormDialogComponent } from 'src/app/components/main/employees/rrhh/skill-form-dialog/skill-form-dialog.component';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { FormDialogModeEnum } from 'src/app/enums/form-dialog-mode.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Skill } from 'src/app/models/skill.model';
import { ApiService, UtilsService } from 'src/app/services';

@Component({
  selector: 'app-skills-list',
  templateUrl: './skills-list.component.html',
  styleUrl: './skills-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
    TypeSafeMatCellDeferred,
  ],
})
export class SkillsListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);

  dataSource = new MatTableDataSource<Skill>([]);

  displayedColumns: string[] = ['name', 'slug', 'actions'];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenSearchFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(SkillFormDialogComponent, {
      width: '480px',
      data: { mode: FormDialogModeEnum.Create },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
          this.fetchData();
        }
      });
  }

  openEditDialog(skill: Skill): void {
    const dialog_ref = this.mat_dialog.open(SkillFormDialogComponent, {
      width: '480px',
      data: { mode: FormDialogModeEnum.Edit, skill },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  deleteSkill(skill: Skill): void {
    if (!confirm(`¿Eliminar la skill "${skill.name}"?`)) {
      return;
    }

    this.api_service.deleteSkill(skill.id).subscribe({
      next: (): void => {
        const current_page = Number(this.filterForm.get('page')?.value ?? 1);
        if (this.dataSource.data.length === 1 && current_page > 1) {
          this.filterForm.patchValue({ page: current_page - 1 }, { emitEvent: false });
        }
        this.fetchData();
        this.snack_bar.open('Skill eliminada', undefined, { duration: 3000 });
      },
    });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'name:asc',
    };

    if (search_term) {
      params.search = search_term;
    }

    this.api_service.getSkills(params).subscribe({
      next: (response: ArrayResponse<Skill>): void => {
        this.dataSource.data = response.data;
        this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
        this.change_detector_ref.markForCheck();
      },
    });
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }
}
